import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import clsx from 'clsx';
import * as R from 'ramda';

import InventoryAssetTile from '@atom/components/common/inventoryAssetTree/InventoryAssetTile';
import InventoryAssetTree from '@atom/components/common/inventoryAssetTree/InventoryAssetTree';
import renameIcon from '@atom/components/common/svgIcons/renameIcon.svg';
import { GET_APPOINTMENT_CONFLICTS } from '@atom/graph/appointments';
import { WORK_ORDER_UPDATE } from '@atom/graph/work';
import { usePreferences } from '@atom/hooks/usePreferences';
import { useSchemas } from '@atom/hooks/useSchemas';
import { useUserProfile } from '@atom/hooks/useUserProfile';
import {
  DatePicker,
  DateTimePicker,
  Icon,
  IconButton,
  Modal,
  Switch,
  TextField,
} from '@atom/mui';
import colors from '@atom/styles/colors';
import {
  AppointmentConflictsConnection,
  AppointmentConflictsConnectionInput,
} from '@atom/types/appointments';
import {
  WorkOrderDetailType,
  WorkOrderType,
  WorkOrderUpdate,
  WorkOrderUpdateInput,
} from '@atom/types/work';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import {
  convertDateToMillisGMTMidday,
  getUpdatedOtherDate,
} from '@atom/utilities/timeUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import AppointmentConflictError from './AppointmentConflictError';

import classes from './workOrderDetailEdit.module.css';

enum View {
  MAIN = 'MAIN',
  ASSET_TREE = 'ASSET_TREE',
  ASSET_CHANGE_CONFIRMATION = 'ASSET_CHANGE_CONFIRMATION',
}

interface RootAsset {
  id: string;
  name: string;
  schemaId: string;
  markerId: number;
  isMaterial?: boolean;
}

const styles = {
  icon: {
    marginRight: '2rem',
  },
  back: {
    marginRight: '1rem',
  },
  modalContent: {
    padding: '2rem',
  },
};

const modalContentStyles: { [key in View]: React.CSSProperties } = {
  [View.MAIN]: {
    ...styles.modalContent,
  },
  [View.ASSET_TREE]: {
    ...styles.modalContent,
    height: '50vh',
  },
  [View.ASSET_CHANGE_CONFIRMATION]: {
    ...styles.modalContent,
  },
};

const modalConfirmTexts: { [key in View]: string } = {
  [View.MAIN]: 'Save',
  [View.ASSET_TREE]: 'Save',
  [View.ASSET_CHANGE_CONFIRMATION]: 'Confirm',
};

interface Props {
  workOrderDetail: WorkOrderDetailType;
  refetch: () => void;
  iconColor?: string;
}

const WorkOrderDetailEdit = ({
  workOrderDetail,
  refetch,
  iconColor = colors.neutral.gray,
}: Props) => {
  const userProfile = useUserProfile();
  const preferences = usePreferences();
  const { schemas = [] } = useSchemas();

  const [open, setOpen] = useState<boolean>(false);
  const [view, setView] = useState<View>(View.MAIN);
  const [name, setName] = useState<string>('');
  const [dueDate, setDueDate] = useState<Date>(null);
  const [plannedStartDate, setPlannedStartDate] = useState<Date>(null);
  const [completionDate, setCompletionDate] = useState<Date>(null);
  const [rootAsset, setRootAsset] = useState<RootAsset>(null);
  const [isLocationBased, setIsLocationBased] = useState<boolean>(
    workOrderDetail.isLocationBased,
  );

  const isRootAssetAdded = !workOrderDetail.inventoryAssetId && !!rootAsset;
  const isRootAssetRemoved = workOrderDetail.inventoryAssetId && !rootAsset;
  const isRootAssetUpdated =
    workOrderDetail.inventoryAssetId &&
    rootAsset &&
    rootAsset.id !== workOrderDetail.inventoryAssetId;
  const isRootAssetChanged =
    isRootAssetAdded || isRootAssetRemoved || isRootAssetUpdated;

  const isWorkNamingDisabled = preferences.workOrders?.workNamingDisabled;

  const workType = R.pathOr(WorkOrderType.DEFAULT, ['type'], workOrderDetail);

  const [getConflicts, { data: conflictData, loading: conflictLoading }] =
    useLazyQuery<
      { appointmentConflicts: AppointmentConflictsConnection },
      { input: AppointmentConflictsConnectionInput }
    >(GET_APPOINTMENT_CONFLICTS, {
      fetchPolicy: 'network-only',
    });

  const assignees = useMemo(() => {
    return workOrderDetail?.tasks?.reduce((acc, task) => {
      return [...acc, ...task?.users.map(user => user?.id)];
    }, []);
  }, [workOrderDetail]);

  useEffect(() => {
    if (
      open &&
      workOrderDetail?.appointmentsEnabled &&
      !isNilOrEmpty(dueDate) &&
      !isNilOrEmpty(plannedStartDate) &&
      !isNilOrEmpty(assignees)
    ) {
      getConflicts({
        variables: {
          input: {
            workOrderId: workOrderDetail?.id,
            plannedStartDate: plannedStartDate?.valueOf(),
            dueDate: dueDate?.valueOf(),
            userIds: assignees,
          },
        },
      });
    }
  }, [workOrderDetail, dueDate, plannedStartDate, open, assignees]);

  const [updateWorkDetail, { loading: workOrderLoading }] = useMutation<
    { workOrderUpdate: WorkOrderUpdate },
    { workOrder: WorkOrderUpdateInput }
  >(WORK_ORDER_UPDATE, {
    onCompleted: () => {
      setOpen(false);
      refetch();
    },
  });

  const rootSchema = useMemo(() => {
    const schemaId = !isNilOrEmpty(rootAsset)
      ? rootAsset?.schemaId
      : workOrderDetail?.inventoryAssetSchemaId;

    return R.find(R.propEq(schemaId, 'id'), schemas);
  }, [rootAsset, workOrderDetail, schemas]);

  const resetState = () => {
    setView(View.MAIN);
    setName(workOrderDetail.originalName);
    setIsLocationBased(workOrderDetail.isLocationBased);
    setDueDate(
      workOrderDetail.dueDate ? new Date(workOrderDetail.dueDate) : null,
    );
    setPlannedStartDate(
      workOrderDetail.plannedStartDate
        ? new Date(workOrderDetail.plannedStartDate)
        : null,
    );
    setCompletionDate(
      workOrderDetail.completionDate
        ? new Date(workOrderDetail.completionDate)
        : null,
    );

    if (workOrderDetail.inventoryAssetId) {
      setRootAsset({
        id: workOrderDetail.inventoryAssetId,
        schemaId: workOrderDetail.inventoryAssetSchemaId,
        name: workOrderDetail.inventoryAssetName,
        markerId: workOrderDetail.inventoryAssetMarkerId,
        isMaterial: rootSchema?.isMaterial,
      });
    } else {
      setRootAsset(null);
    }
  };

  useEffect(() => {
    resetState();
  }, [workOrderDetail]);

  const handleDueDateTimeChange = (date?: Date) => {
    if (!date || date.toString() === 'Invalid Date') {
      setDueDate(null);
    } else {
      // Ensures date times are on the same day
      const updatedPlannedStartDate = getUpdatedOtherDate(
        date,
        plannedStartDate,
      );

      setDueDate(date);
      setPlannedStartDate(updatedPlannedStartDate);
    }
  };

  const handlePlannedStartDateTimeChange = (date?: Date) => {
    if (!date || date.toString() === 'Invalid Date') {
      setPlannedStartDate(null);
    } else {
      // Ensures date times are on the same day
      const updatedDueDate = getUpdatedOtherDate(date, dueDate);

      setPlannedStartDate(date);
      setDueDate(updatedDueDate);
    }
  };

  const updateWorkOrder = () => {
    return updateWorkDetail({
      variables: {
        workOrder: {
          id: workOrderDetail.id,
          name,
          ...R.reject(R.isNil, {
            dueDate: dueDate ? dueDate.valueOf() : '',
            plannedStartDate: plannedStartDate
              ? plannedStartDate.valueOf()
              : null,
            completionDate: completionDate
              ? convertDateToMillisGMTMidday(completionDate)
              : null,
          }),
          ...(isLocationBased !== workOrderDetail.isLocationBased && {
            isLocationBased,
          }),
          ...(isLocationBased &&
            !workOrderDetail.location && {
              location: userProfile.location,
            }),
          ...(isRootAssetChanged && {
            inventoryAssetId: rootAsset?.id || '',
          }),
        },
      },
    });
  };

  const handleSubmit = () => {
    if (!name) {
      return null;
    }

    if (view === View.MAIN && (isRootAssetUpdated || isRootAssetRemoved)) {
      return setView(View.ASSET_CHANGE_CONFIRMATION);
    }

    return updateWorkOrder();
  };

  const handleCompletionDateChange = (date?: Date) => {
    if (!date) {
      setCompletionDate(null);
    } else if (!Number.isNaN(date.valueOf())) {
      setCompletionDate(date);
    }
  };

  const handleDueDateChange = (date?: Date) => {
    if (!date) {
      setDueDate(null);
    } else if (!Number.isNaN(date.valueOf())) {
      setDueDate(date);
    }
  };

  const handleRootAssetChange = (asset: RootAsset) => {
    setIsLocationBased(!asset?.isMaterial);
    setRootAsset(asset);
    setView(View.MAIN);
  };

  const handleRootAssetRemove = () => {
    setRootAsset(null);
    setIsLocationBased(false);
  };

  const modalHeaders: { [key in View]: React.ReactNode } = useMemo(() => {
    return {
      [View.MAIN]: 'Edit Work',
      [View.ASSET_TREE]: (
        <>
          <IconButton style={styles.back} onClick={() => setView(View.MAIN)}>
            <Icon>arrow_back</Icon>
          </IconButton>
          Edit Asset
        </>
      ),
      [View.ASSET_CHANGE_CONFIRMATION]: 'Remove Asset?',
    };
  }, [setView]);

  const isValid = () => {
    return (
      name &&
      (workOrderDetail?.appointmentsEnabled
        ? !isNilOrEmpty(dueDate) && !isNilOrEmpty(plannedStartDate)
        : true) &&
      (workOrderDetail?.appointmentsEnabled &&
      !isNilOrEmpty(dueDate) &&
      !isNilOrEmpty(plannedStartDate)
        ? plannedStartDate.valueOf() <= dueDate.valueOf()
        : true)
    );
  };

  const showAssetAndLocation =
    workType === WorkOrderType.DEFAULT ||
    isNilOrEmpty(workOrderDetail?.workTemplate);

  const assetData = rootAsset
    ? {
        ...rootAsset,
        isMaterial: rootSchema?.isMaterial,
        colorId: rootSchema?.colorId,
      }
    : null;

  const assetRemovalDisabled = R.pathOr(
    false,
    ['workOrders', 'assetRemovalDisabled'],
    preferences,
  );

  const error = useMemo(() => {
    if (isNilOrEmpty(plannedStartDate) || isNilOrEmpty(dueDate)) {
      return false;
    }

    return plannedStartDate.valueOf() > dueDate.valueOf();
  }, [dueDate, plannedStartDate]);

  // If work order has no assignees, then there cannot be any conflicts
  const appointmentConflicts = isNilOrEmpty(assignees)
    ? []
    : conflictData?.appointmentConflicts?.appointmentConflicts;

  const getContent = () => {
    const content = {
      [View.MAIN]: (
        <div>
          <div className={clsx(classes.modalRow, classes.name)}>
            <Icon style={styles.icon}>
              <img src={renameIcon} />
            </Icon>
            <TextField
              id="workOrderName"
              label="Work Name"
              value={name}
              onChange={event => setName(event.target.value)}
              disabled={!!isWorkNamingDisabled}
              required
            />
          </div>
          {workOrderDetail?.appointmentsEnabled ? (
            <>
              <div className={clsx(classes.modalRow, classes.dueDate)}>
                <Icon style={styles.icon}>event</Icon>
                <DateTimePicker
                  key="plannedStartDate"
                  label="Planned Start Date *"
                  value={plannedStartDate}
                  onChange={handlePlannedStartDateTimeChange}
                  hideClear
                />
              </div>
              <div className={clsx(classes.modalRow, classes.dueDate)}>
                <div className={classes.iconSpacer} />
                <DateTimePicker
                  key="dueDate"
                  label="Due Date *"
                  value={dueDate}
                  onChange={handleDueDateTimeChange}
                  helperText={
                    error && 'Due date cannot be before planned start date.'
                  }
                  error={error}
                  hideClear
                />
              </div>
              {!isNilOrEmpty(appointmentConflicts) && (
                <AppointmentConflictError
                  appointmentConflicts={appointmentConflicts}
                />
              )}
            </>
          ) : (
            <div className={clsx(classes.modalRow, classes.dueDate)}>
              <Icon style={styles.icon}>event</Icon>
              <DatePicker
                label="Due Date"
                value={dueDate}
                onChange={handleDueDateChange}
              />
            </div>
          )}
          <div className={clsx(classes.modalRow, classes.noIcon)}>
            <DatePicker
              value={completionDate}
              onChange={handleCompletionDateChange}
              label="Completed Date"
              style={{ marginBottom: '1rem' }}
            />
          </div>
          {showAssetAndLocation && (
            <>
              <div className={clsx(classes.modalRow, classes.asset)}>
                <Icon style={styles.icon}>category</Icon>
                <InventoryAssetTile
                  asset={assetData}
                  onClick={() => setView(View.ASSET_TREE)}
                  assetRemovalDisabled={assetRemovalDisabled}
                  onRemove={handleRootAssetRemove}
                />
              </div>
              <div className={classes.toggleContainer}>
                <Switch
                  checked={isLocationBased}
                  onChange={() => setIsLocationBased(!isLocationBased)}
                  name="isLocationBased"
                  disabled={
                    (!R.isEmpty(workOrderDetail.inventoryAssetId) &&
                      !!rootAsset) ||
                    !!rootAsset
                  }
                  label="Location"
                />
              </div>
            </>
          )}
        </div>
      ),
      [View.ASSET_TREE]: (
        <InventoryAssetTree
          onAssetClick={handleRootAssetChange}
          search="assets"
          // if the work template for this work has a schema ID,
          // we only allow the user to select assets that have that schema.
          {...(workOrderDetail.workTemplate?.schemaId && {
            schemaId: workOrderDetail.workTemplate?.schemaId,
          })}
        />
      ),
      [View.ASSET_CHANGE_CONFIRMATION]: (
        <div>
          Removing the asset will remove assets and forms of the same asset type
          from all tasks. Are you sure?
        </div>
      ),
    };

    return content[view];
  };

  const loading = conflictLoading || workOrderLoading;

  return (
    <div className={classes.iconModalContainer}>
      <IconButton
        edge="end"
        onClick={() => setOpen(true)}
        tooltip="Edit"
        disabled={doesNotHaveRolePermissions(ROLE_SETS.INSPECTOR)}
      >
        <Icon color={iconColor}>edit</Icon>
      </IconButton>
      <Modal
        title={modalHeaders[view] || 'Edit Work'}
        open={open}
        onCancel={() => setOpen(false)}
        onConfirm={handleSubmit}
        confirmButtonText={modalConfirmTexts[view] || 'Save'}
        loading={loading || !isValid()}
        onExited={resetState}
        contentStyle={modalContentStyles[view] || styles.modalContent}
      >
        {getContent()}
      </Modal>
    </div>
  );
};

export default WorkOrderDetailEdit;
